import React from 'react';
import { ArrowUpCircleIcon, ArrowDownCircleIcon, LinkIcon, GlobeAltIcon } from '@heroicons/react/24/solid';
import Tippy from '@tippyjs/react';
import GlassyBadge from '../common/GlassyBadge';
import GlassyIcon from '../common/GlassyIcon';
import FuriganaToggle from './FuriganaToggle';
import { srsMapping, srsPoints } from '../../utils/mappings';

const InteractiveCardHeader = ({ level, levelUpAlertState, isSubmitted = true, politeness, onPolitenessClick, currentItem, onSeeAlso, onExamples, onCulturalContext, reducedAnimations = false }) => {
  const { show, type, level: alertLevel, prevLevel } = levelUpAlertState;
  const ptsDiff = (prevLevel !== undefined && alertLevel !== undefined && alertLevel !== '')
    ? (srsPoints[alertLevel] ?? 0) - (srsPoints[prevLevel] ?? 0)
    : null;

  const getHeaderContainerClasses = () => {
    const baseClasses = 'min-h-20 relative w-full flex items-center justify-between p-3 transition-all duration-500 ease-in-out overflow-hidden';

    if (!show || !isSubmitted) {
      return `${baseClasses} bg-transparent`;
    }

    if (type === 'up') {
      // Changed to a solid green background
      return `${baseClasses} bg-green-500/20`;
    }

    if (type === 'down') {
      // Changed to a solid red background
      return `${baseClasses} bg-red-500/20`;
    }

    return `${baseClasses}`;
  };

  const isDown = type === 'down';
  const iconColor = isDown ? 'text-red-500' : 'text-emerald-500';
  const titleText = isDown ? 'LEVEL DOWN' : 'LEVEL UP';
  const IconComponent = isDown ? ArrowDownCircleIcon : ArrowUpCircleIcon;

  return (
    <div className={getHeaderContainerClasses()}>
      {/* The animated background glow effect div has been removed. */}

      {/* Left side - N-Level Badge, Politeness Badge, and Icons */}
      <div className="z-10 relative flex flex-col sm:flex-row sm:items-center items-start gap-1.5">
        <div
          style={{
            opacity: isSubmitted ? 1 : 0,
            visibility: isSubmitted ? 'visible' : 'hidden',
            display: isSubmitted ? 'block' : 'none',
          }}
        >
          <GlassyBadge level={level} size="sm" variant="solid" className="sm:px-2.5 sm:py-1">
            N{level}
          </GlassyBadge>
        </div>
        {!currentItem?.word && currentItem?.kanji_complexity != null && (
          <div
            style={{
              opacity: isSubmitted ? 1 : 0,
              visibility: isSubmitted ? 'visible' : 'hidden',
              display: isSubmitted ? 'block' : 'none',
            }}
          >
            <GlassyBadge color="blue" size="sm" variant="solid" className="sm:px-2.5 sm:py-1">
              {currentItem.kanji_complexity === 61 ? '漢61+' : `漢${currentItem.kanji_complexity}`}
            </GlassyBadge>
          </div>
        )}
        {politeness && (
          <div
            style={{
              opacity: isSubmitted ? 1 : 0,
              visibility: isSubmitted ? 'visible' : 'hidden',
              display: isSubmitted ? 'block' : 'none',
            }}
          >
            <button onClick={onPolitenessClick} className="hover:scale-105 active:scale-95 transition-all duration-200" disabled={!onPolitenessClick}>
              <GlassyBadge politeness={politeness.toLowerCase()} size="sm" variant="solid" bounce={!!onPolitenessClick && !reducedAnimations} className={`sm:px-2.5 sm:py-1 ${onPolitenessClick ? 'cursor-pointer' : ''}`}>
                {politeness.charAt(0).toUpperCase() + politeness.slice(1)}
              </GlassyBadge>
            </button>
          </div>
        )}

        {/* Examples Badge - only show for words */}
        {currentItem?.word && onExamples && (
          <div
            style={{
              opacity: isSubmitted ? 1 : 0,
              visibility: isSubmitted ? 'visible' : 'hidden',
              display: isSubmitted ? 'block' : 'none',
            }}
          >
            <button onClick={onExamples} className="hover:scale-105 active:scale-95 transition-all duration-200">
              <GlassyBadge color="orange" size="sm" variant="solid" bounce={!reducedAnimations} className="cursor-pointer sm:px-2.5 sm:py-1">
                Examples
              </GlassyBadge>
            </button>
          </div>
        )}
      </div>

      {/* Center - Level Up Alert */}
      <div
        className="absolute inset-0 flex items-center justify-center transition-all duration-500 ease-in-out"
        style={{
          opacity: show && isSubmitted ? 1 : 0,
          transform: show && isSubmitted ? 'scale(1)' : 'scale(0.95)',
          pointerEvents: show && isSubmitted ? 'auto' : 'none',
          display: isSubmitted ? 'flex' : 'none',
        }}
        aria-live="polite"
      >
        <div className="flex items-center gap-3 px-6 py-2 rounded-full bg-background-secondary/80">
          {/* Icon with subtle animation */}
          <IconComponent className={`h-7 w-7 ${iconColor} ${show && !reducedAnimations ? 'animate-bounce' : ''}`} />

          {/* Main alert content */}
          <div className="flex items-center gap-3">
            <div className="flex flex-col items-center">
              <span className="text-xs font-bold uppercase tracking-wider text-content-secondary opacity-80">{titleText}</span>
              <div className="flex items-center gap-2 mt-0.5">
                <GlassyBadge srsStage={alertLevel} size="md" variant="solid">
                  {srsMapping[alertLevel]}
                </GlassyBadge>
                {ptsDiff !== null && (
                  <span className={`text-sm font-bold ${!isDown ? 'text-emerald-500' : 'text-red-500'}`}>
                    {ptsDiff > 0 ? `+${ptsDiff}` : ptsDiff} pts
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Icons and Furigana Toggle */}
      <div className="z-10 relative flex items-center gap-2">
        {/* Icons Section */}
        {/* See Also Icon */}
        {currentItem && (currentItem?.see_also?.length > 0 || currentItem?.antonyms?.length > 0) && onSeeAlso && (
          <div className="z-10">
            <Tippy
              content={
                currentItem?.antonyms?.length > 0 && currentItem?.see_also?.length > 0
                  ? 'See Also & Antonyms'
                  : currentItem?.antonyms?.length > 0
                  ? 'Antonyms'
                  : 'See Also'
              }
              placement="top"
              arrow={false}
              delay={[500, 0]}
            >
              <button onClick={onSeeAlso} className="hover:scale-110 active:scale-95 transition-all duration-200">
                <GlassyIcon 
                  icon={LinkIcon} 
                  size="xs" 
                  {...(() => {
                    const hasSeeAlso = currentItem?.see_also?.length > 0;
                    const hasAntonyms = currentItem?.antonyms?.length > 0;
                    
                    if (hasSeeAlso && hasAntonyms) {
                      return {
                        customGradient: 'linear-gradient(135deg, var(--color-glassy-blue), var(--color-glassy-red))',
                        customShadow: '0 4px 12px rgba(59, 130, 246, 0.3), 0 4px 12px rgba(239, 68, 68, 0.3)',
                        className: `${!reducedAnimations ? 'animate-bounce' : ''} border-none`,
                      };
                    } else if (hasAntonyms) {
                      return { color: 'red', className: !reducedAnimations ? 'animate-bounce' : '' };
                    } else {
                      return { color: 'blue', className: !reducedAnimations ? 'animate-bounce' : '' };
                    }
                  })()} 
                />
              </button>
            </Tippy>
          </div>
        )}

        {/* Cultural Context Icon */}
        {currentItem?.cultural_context && onCulturalContext && (
          <div className="z-10">
            <Tippy content="Cultural Context" placement="top" arrow={false} delay={[500, 0]}>
              <button onClick={onCulturalContext} className="hover:scale-110 active:scale-95 transition-all duration-200">
                <GlassyIcon icon={GlobeAltIcon} size="xs" color="green" className={!reducedAnimations ? 'animate-bounce' : ''} />
              </button>
            </Tippy>
          </div>
        )}
        
        <FuriganaToggle />
      </div>
    </div>
  );
};

export default InteractiveCardHeader;
